import { HttpAgent } from "@ag-ui/client";
import type { AgentSubscriber, Message } from "@ag-ui/client";
import { agentConfig } from "@/shared/config/agentConfig";
import { getAgentAuthHeaders } from "./agentApi";

/**
 * AG-UI streaming transport against the Tower.Agent /agui endpoint (#672).
 * The HttpAgent owns the client-side message history (the server is stateless
 * per-run and re-reads it), so one stream lives per conversation and is seeded
 * from persisted turns when an old conversation is resumed.
 * The @ag-ui/client types stay behind this module; callers see the plain
 * shapes below.
 */

export interface HistoryMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AgentToolDeclaration {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface AgentToolCall {
  toolCallId: string;
  name: string;
  args: Record<string, unknown>;
}

export interface AgentStreamHandlers {
  /** The current text message's buffer so far (not a delta). */
  onTextContent(buffer: string): void;
  /** The finished text message's buffer, which can be empty. */
  onTextEnd(buffer: string): void;
  onToolCallStart(name: string): void;
  onToolCallEnd(call: AgentToolCall): void;
  onRunError(message: string | undefined): void;
}

export interface AgentStream {
  readonly threadId: string;
  /**
   * One request/response round; tools are declared to the model for this round
   * only. `isCancelled` is polled after the per-run auth headers resolve, so a
   * run superseded during that await never starts a request on the shared agent.
   */
  run(
    forwardedProps: Record<string, unknown>,
    tools: AgentToolDeclaration[],
    handlers: AgentStreamHandlers,
    isCancelled?: () => boolean,
  ): Promise<void>;
  addUserMessage(text: string): void;
  /** Appends tool results for the next round, each after an assistant message that issued its call. */
  addToolResults(results: ReadonlyArray<{ call: AgentToolCall; content: string }>): void;
  /** Captures the message history; the returned function restores it. */
  checkpoint(): () => void;
  /**
   * Drops assistant tool calls with no matching tool result from the history, so
   * an interrupted run cannot leave an unanswered call that makes the model API
   * reject the next request.
   */
  pruneDanglingToolCalls(): void;
  abort(): void;
}

export function createAgentStream(threadId: string, history: HistoryMessage[]): AgentStream {
  const initialMessages: Message[] = history.map((m, i) => ({
    id: `h-${i}`,
    role: m.role,
    content: m.content,
  }));

  const agent = new HttpAgent({
    url: `${agentConfig.baseUrl}/agui`,
    threadId,
    initialMessages,
  });

  /**
   * Guarantees every call's parent exists in the history: the HttpAgent does not
   * reliably record an assistant tool-call message for every streamed call, and
   * a tool result whose call is missing makes the model API reject the whole
   * next request ("role 'tool' must be a response to ... 'tool_calls'").
   */
  function ensureToolCallParents(calls: AgentToolCall[]): void {
    const knownCallIds = new Set(
      agent.messages.flatMap((m) =>
        m.role === "assistant" ? (m.toolCalls ?? []).map((tc) => tc.id) : [],
      ),
    );
    const missing = calls.filter((call) => !knownCallIds.has(call.toolCallId));
    if (missing.length > 0) {
      agent.addMessage({
        id: crypto.randomUUID(),
        role: "assistant",
        toolCalls: missing.map((call) => ({
          id: call.toolCallId,
          type: "function" as const,
          function: { name: call.name, arguments: JSON.stringify(call.args ?? {}) },
        })),
      });
    }
  }

  return {
    threadId,

    async run(forwardedProps, tools, handlers, isCancelled) {
      // Headers re-resolve per run so a renewed token is picked up (#668 finding).
      const headers = await getAgentAuthHeaders();
      // A run superseded while the headers were resolving must not touch the
      // shared agent: setting its headers or starting a request here would race
      // the replacement run that already began on the same agent.
      if (isCancelled?.()) {
        return;
      }
      agent.headers = headers;

      const subscriber: AgentSubscriber = {
        onTextMessageContentEvent: ({ textMessageBuffer }) => {
          handlers.onTextContent(textMessageBuffer);
        },
        onTextMessageEndEvent: ({ textMessageBuffer }) => {
          handlers.onTextEnd(textMessageBuffer);
        },
        onToolCallStartEvent: ({ event }) => {
          handlers.onToolCallStart(event.toolCallName);
        },
        onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) => {
          handlers.onToolCallEnd({
            toolCallId: event.toolCallId,
            name: toolCallName,
            args: (toolCallArgs ?? {}) as Record<string, unknown>,
          });
        },
        onRunErrorEvent: ({ event }) => {
          handlers.onRunError(event.message);
        },
      };

      await agent.runAgent({ forwardedProps, tools }, subscriber);
    },

    addUserMessage(text) {
      agent.addMessage({ id: crypto.randomUUID(), role: "user", content: text });
    },

    addToolResults(results) {
      ensureToolCallParents(results.map((r) => r.call));
      for (const { call, content } of results) {
        agent.addMessage({ id: crypto.randomUUID(), role: "tool", content, toolCallId: call.toolCallId });
      }
    },

    checkpoint() {
      const baseline = [...agent.messages];
      return () => {
        agent.messages = baseline;
      };
    },

    pruneDanglingToolCalls() {
      const answeredCallIds = new Set(
        agent.messages.flatMap((m) => (m.role === "tool" && m.toolCallId ? [m.toolCallId] : [])),
      );
      const pruned: Message[] = [];
      for (const message of agent.messages) {
        if (message.role !== "assistant" || !message.toolCalls || message.toolCalls.length === 0) {
          pruned.push(message);
          continue;
        }
        const keptCalls = message.toolCalls.filter((call) => answeredCallIds.has(call.id));
        if (keptCalls.length === message.toolCalls.length) {
          pruned.push(message);
          continue;
        }
        // An assistant message reduced to only unanswered calls carries no useful
        // history; drop it unless it still has answered calls or text content.
        if (keptCalls.length === 0 && !message.content) {
          continue;
        }
        pruned.push({ ...message, toolCalls: keptCalls });
      }
      agent.messages = pruned;
    },

    abort() {
      agent.abortRun();
    },
  };
}
